import React from 'react'
import { Chip, Stack } from '@common/material'
import { ChipProps } from '@mui/material'
import { TaskStatus } from '@core/entities/task/TaskBase'
import { Task } from '@core/entities/task/Task'
import { SxProps, Theme } from '@mui/material/styles'
import { statuses } from '@core/theme/statuses'

type TaskStatusChipProps = {
  status: TaskStatus
  label?: string
  hasRequestedChanges?: boolean
  layout?: 'horizontal' | 'vertical'
  size?: ChipProps['size']
  sx?: SxProps<Theme>
  onClick?: () => void
}

export const TaskStatusChip: React.FC<TaskStatusChipProps> = ({
  status,
  label,
  hasRequestedChanges,
  layout = 'horizontal', // default to horizontal for backwards compatibility
  size,
  sx,
  onClick,
}) => {
  const getChipProps = (status: TaskStatus) => {
    switch (status) {
      case 'DRAFT':
        return {
          color: Task.getStatusChipColor(status),
          label: statuses.draft.label,
        }
      case 'IN_PROGRESS':
        return {
          color: Task.getStatusChipColor(status),
          label: label || statuses.inProgress.label,
        }
      case 'AWAITING_APPROVAL':
        return {
          color: Task.getStatusChipColor(status),
          label: label || statuses.approval.label,
        }
      case 'CANCELLED':
        return {
          color: Task.getStatusChipColor(status),
          label: statuses.cancelled.label,
        }
      case 'DONE':
        return {
          color: Task.getStatusChipColor(status),
          label: statuses.completed.label,
        }

      default:
        return null
    }
  }

  const chipProps = getChipProps(status)

  if (!chipProps) return null

  return (
    <Stack
      direction={layout === 'horizontal' ? 'row' : 'column'}
      spacing={1}
      alignItems={layout === 'vertical' ? 'flex-start' : 'center'}
    >
      <Chip
        data-test='task-status-chip'
        variant='filled'
        {...chipProps}
        sx={sx}
        size={size}
        onClick={onClick}
      />
      {hasRequestedChanges && (
        <Chip
          variant='outlined'
          color={statuses.changesRequested.color}
          label={statuses.changesRequested.label}
          sx={{
            borderColor: `${statuses.changesRequested.color}.main`,
            color: `${statuses.changesRequested.color}.main`,
            ...sx,
          }}
          size={size}
          onClick={onClick}
        />
      )}
    </Stack>
  )
}
